import { isFieldEnabled, type LogicField } from './fieldVisibility';
import { checkGuestDomain, type GuestDomainMode } from './freeEmailDomains';

export type GuestQuestionLike = {
  _id?: unknown;
  id?: unknown;
  type?: string | null;
  required?: boolean | null;
  element?: string | null;
  options?: LogicField['options'];
  showHideLogic?: LogicField['showHideLogic'];
  defaultValue?: { enabled?: boolean | null; value?: unknown; values?: unknown[] | null } | null;
};

export type GuestAnswerLike = { fieldId?: unknown; value?: unknown; values?: unknown[] | null };

export type GuestLike = {
  name?: string | null;
  email?: string | null;
  answers?: GuestAnswerLike[] | null;
  ticketType?: unknown;
  position?: number | null;
  flagged?: boolean | null;
  reviewedAt?: unknown;
};

export const GUEST_STATUSES = ['complete', 'missing', 'flagged', 'optional'] as const;

export const GUEST_INPUT_LIMITS = {
  name: 200,
  email: 320,
  answerValue: 5000,
  answerValuesItem: 1000,
  answerValues: 100,
  answers: 100,
} as const;

// Every event's guest questions start with these two, which can't be removed or made optional.
export const DEFAULT_GUEST_QUESTIONS = [
  { label: 'Name', element: 'text', type: 'name', required: true, order: 0 },
  { label: 'Email address', element: 'email', type: 'email', required: true, order: 1 },
] as const;
export type GuestStatus = (typeof GUEST_STATUSES)[number];

const hasText = (value: unknown) => typeof value === 'string' && value.trim() !== '';
const idOf = (value: unknown) => String((value as { _id?: unknown })?._id ?? value ?? '');

const questionId = (question: GuestQuestionLike) => idOf(question._id ?? question.id);

const answerFor = (guest: GuestLike, question: GuestQuestionLike) => {
  if (question.type === 'name') return { value: guest.name };
  if (question.type === 'email') return { value: guest.email };
  return (guest.answers ?? []).find(a => idOf(a.fieldId) === questionId(question));
};

/** The guest's answers in the shape conditional logic reads, with every id as a string. */
function guestLogicFields(questions: GuestQuestionLike[], guest: GuestLike) {
  const fields: LogicField[] = questions.map(question => ({
    _id: questionId(question),
    element: question.element,
    options: (question.options ?? []).map(o => ({ ...o, _id: idOf(o?._id) })),
    showHideLogic: question.showHideLogic && {
      ...question.showHideLogic,
      element: { elementId: idOf(question.showHideLogic.element?.elementId) },
    },
  }));
  const values = Object.fromEntries(
    fields.map((field, index) => {
      const answer = answerFor(guest, questions[index]);
      return [field._id as string, { ...field, value: answer?.values ?? answer?.value }];
    }),
  );
  return { fields, values };
}

/**
 * Name and email live on the guest itself; every other question is an answer. A required
 * question that conditional logic hides from this guest isn't needed.
 */
export function guestIsComplete(
  questions: GuestQuestionLike[],
  guest: GuestLike | null | undefined,
): boolean {
  if (!guest) return false;
  const logic = guestLogicFields(questions, guest);
  return questions.every((question, index) => {
    if (!question.required) return true;
    if (!isFieldEnabled(logic.fields[index], logic.values, logic.fields)) return true;
    const answer = answerFor(guest, question) as GuestAnswerLike | undefined;
    return hasText(answer?.value) || (answer?.values ?? []).some(hasText);
  });
}

export function guestQuestionDefault(question: GuestQuestionLike): GuestAnswerLike | null {
  const preset = question.defaultValue;
  if (!preset?.enabled || question.type === 'name' || question.type === 'email') return null;
  const fieldId = questionId(question);
  if (question.element === 'multi-select') {
    const offered = new Set((question.options ?? []).map(o => o.value || o.label));
    const values = (preset.values ?? []).map(String).filter(v => hasText(v) && offered.has(v));
    return values.length ? { fieldId, values } : null;
  }
  const value = preset.value == null ? '' : String(preset.value);
  return hasText(value) ? { fieldId, value } : null;
}

/** A guest with each question they didn't answer set to its default, as a hidden one needs. */
export function withGuestDefaults<T extends GuestLike>(
  questions: GuestQuestionLike[],
  guest: T,
): T {
  const answered = new Set((guest.answers ?? []).map(a => idOf(a.fieldId)));
  const defaults = questions
    .filter(question => !answered.has(questionId(question)))
    .map(guestQuestionDefault)
    .filter((answer): answer is GuestAnswerLike => answer !== null);
  return defaults.length ? { ...guest, answers: [...(guest.answers ?? []), ...defaults] } : guest;
}

/**
 * A guest's name when the event asks First name and Last name instead of Name: the two put
 * together, so the ticket, its PDF, check-in and the API still have it.
 */
export function withGuestName<T extends GuestLike>(questions: GuestQuestionLike[], guest: T): T {
  if (questions.some(question => question.type === 'name')) return guest;
  const part = (type: string) => {
    const question = questions.find(q => q.type === type);
    const answer = question && (answerFor(guest, question) as GuestAnswerLike | undefined);
    return typeof answer?.value === 'string' ? answer.value.trim() : '';
  };
  const name = [part('first-name'), part('last-name')].filter(Boolean).join(' ');
  return name ? { ...guest, name } : guest;
}

export function guestIsBlank(guest: GuestLike | null | undefined): boolean {
  if (!guest) return true;
  return (
    !hasText(guest.name) &&
    !hasText(guest.email) &&
    !(guest.answers ?? []).some(a => hasText(a.value) || (a.values ?? []).some(hasText))
  );
}

/**
 * One guest row per ticket, in the order the ticket types are listed. Existing rows keep their
 * type; when a quantity drops, that type's blank rows go first, then rows from the end.
 */
export function guestRowsForTickets<T extends GuestLike>(
  ticketTypeIds: unknown[],
  quantities: Record<string, number>,
  existing: T[] = [],
  blankRow: (ticketType: string) => T = ticketType => ({ ticketType }) as T,
): T[] {
  const rows = ticketTypeIds.flatMap(rawId => {
    const ticketType = idOf(rawId);
    const quantity = Math.max(0, Math.floor(quantities[ticketType] ?? 0));
    const ofType = existing.filter(row => idOf(row.ticketType) === ticketType);
    while (ofType.length > quantity) {
      let drop = -1;
      for (let i = ofType.length - 1; i >= 0; i -= 1) {
        if (guestIsBlank(ofType[i])) {
          drop = i;
          break;
        }
      }
      ofType.splice(drop === -1 ? ofType.length - 1 : drop, 1);
    }
    while (ofType.length < quantity) ofType.push(blankRow(ticketType));
    return ofType;
  });
  return rows.map((row, index) => ({ ...row, position: index + 1 }));
}

/** Positions (1-based) of guests whose email an earlier guest in the booking already uses. */
export function duplicateGuestEmails(guests: GuestLike[]): number[] {
  const seen = new Set<string>();
  const duplicates: number[] = [];
  guests.forEach((guest, index) => {
    const email = (guest.email ?? '').trim().toLowerCase();
    if (!email) return;
    if (seen.has(email)) duplicates.push(index + 1);
    seen.add(email);
  });
  return duplicates;
}

/**
 * Missing wins over flagged: the booker is still asked (reminders and the Missing filter follow it)
 * while the flag shows on the guest itself. A reviewed guest doesn't flag.
 */
// `optional`: the customer isn't asked (a booking from before the event collected details, or one
// the merchant fills in themselves), so unfilled guests are not missing. A flag still needs review.
export function bookingGuestStatus({
  questions,
  guests,
  ticketCount,
  optional = false,
}: {
  questions: GuestQuestionLike[];
  guests: GuestLike[];
  ticketCount: number;
  optional?: boolean;
}): GuestStatus {
  const named = guests.filter(guest => guestIsComplete(questions, guest)).length;
  const flagged = guests.some(guest => guest.flagged && !guest.reviewedAt);
  if (optional) return flagged ? 'flagged' : named < ticketCount ? 'optional' : 'complete';
  if (named < ticketCount) return 'missing';
  return flagged ? 'flagged' : 'complete';
}

export const GUEST_ERROR_MESSAGES = {
  name: 'Enter a name.',
  email: 'Enter an email address.',
  invalidEmail: 'Enter a valid email address.',
  duplicateEmail: 'This email is already used for another attendee.',
  answer: 'Enter an answer.',
  everyGuest: 'Add details for every attendee.',
} as const;

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export type GuestRowErrors = {
  name?: string;
  email?: string;
  /** By question id. */
  answers?: Record<string, string>;
};

/** A required answer that conditional logic hides isn't needed. `flagged`: the domain rule would flag it. */
export function guestRowErrors({
  questions,
  guests,
  index,
  bookerEmail,
  emailDomains,
}: {
  questions: GuestQuestionLike[];
  guests: GuestLike[];
  index: number;
  bookerEmail: string;
  emailDomains: GuestDomainMode;
}): { errors: GuestRowErrors; flagged: boolean } {
  const guest = guests[index] ?? {};
  const errors: GuestRowErrors = {};
  const logic = guestLogicFields(questions, guest);
  let flagged = false;

  questions.forEach((question, at) => {
    if (!question.required || question.type === 'email') return;
    if (!isFieldEnabled(logic.fields[at], logic.values, logic.fields)) return;
    if (question.type === 'name') {
      if (!hasText(guest.name)) errors.name = GUEST_ERROR_MESSAGES.name;
      return;
    }
    const answer = answerFor(guest, question) as GuestAnswerLike | undefined;
    if (!hasText(answer?.value) && !(answer?.values ?? []).some(hasText)) {
      errors.answers = { ...errors.answers, [questionId(question)]: GUEST_ERROR_MESSAGES.answer };
    }
  });

  const email = (guest.email ?? '').trim().toLowerCase();
  if (!email) {
    errors.email = GUEST_ERROR_MESSAGES.email;
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = GUEST_ERROR_MESSAGES.invalidEmail;
  } else {
    const domain = checkGuestDomain({ bookerEmail, guestEmail: email, mode: emailDomains });
    if ('message' in domain) errors.email = domain.message;
    else if (
      guests.some(
        (other, at) => at !== index && (other?.email ?? '').trim().toLowerCase() === email,
      )
    ) {
      errors.email = GUEST_ERROR_MESSAGES.duplicateEmail;
    } else flagged = Boolean(domain.flagged);
  }

  return { errors, flagged };
}

export const hasGuestRowErrors = (errors: GuestRowErrors) =>
  Boolean(errors.name || errors.email || Object.keys(errors.answers ?? {}).length);
